import type {CreateProjectFormValues} from './create-project-form-values'

export const CREATE_PROJECT_STEP_COUNT = 4

export const CREATE_PROJECT_STEP = {
    customer: 0,
    projectDetails: 1,
    billingTerms: 2,
    review: 3,
} as const

function validateCustomer(values: CreateProjectFormValues) {
    const errors: Record<string, string> = {}

    if (!values.customerId) {
        errors['customerId'] = 'Customer is required'
    }

    return errors
}

function validateProjectDetails(values: CreateProjectFormValues) {
    const errors: Record<string, string> = {}

    if (!values.name.trim()) {
        errors['name'] = 'Order name is required'
    }

    if (!values.internalContactId) {
        errors['internalContactId'] = 'Internal contact is required'
    }

    return errors
}

function validateBillingTerms(
    values: CreateProjectFormValues,
    activeMinimumHourlyRate?: number | null,
) {
    const errors: Record<string, string> = {}

    if (values.billingMethod === 'hourly_transportation') {
        if (values.billingTerms.transportationHourlyRate == null) {
            errors['billingTerms.transportationHourlyRate'] = 'All-in hourly rate is required'
        } else if (values.billingTerms.transportationHourlyRate < 0) {
            errors['billingTerms.transportationHourlyRate'] = 'Hourly rate must not be negative'
        } else if (values.billingTerms.transportationHourlyRate === 0) {
            errors['billingTerms.transportationHourlyRate'] =
                'All-in hourly rate must be greater than $0'
        } else if (
            activeMinimumHourlyRate != null &&
            values.billingTerms.transportationHourlyRate < activeMinimumHourlyRate &&
            !values.billingTerms.managerApprovedBelowMinimumRate
        ) {
            errors['billingTerms.managerApprovedBelowMinimumRate'] =
                'Confirm manager approval for the below-minimum rate'
        }
    }

    if (values.billingMethod === 'per_load') {
        if (values.billingTerms.ratePerLoad == null) {
            errors['billingTerms.ratePerLoad'] = 'Rate per load is required'
        } else if (values.billingTerms.ratePerLoad < 0) {
            errors['billingTerms.ratePerLoad'] = 'Rate per load must not be negative'
        }
    }

    if (values.billingMethod === 'flat_rate') {
        if (values.billingTerms.flatRateAmount == null) {
            errors['billingTerms.flatRateAmount'] = 'Flat rate amount is required'
        } else if (values.billingTerms.flatRateAmount < 0) {
            errors['billingTerms.flatRateAmount'] = 'Flat rate must not be negative'
        }
    }

    values.billingTerms.disposalFees.forEach((fee, index) => {
        if (!fee.wasteType.trim()) {
            errors[`billingTerms.disposalFees.${index}.wasteType`] = 'Waste type is required'
        }
        if (!fee.unit.trim()) {
            errors[`billingTerms.disposalFees.${index}.unit`] = 'Unit is required'
        }
        if (fee.unitRate == null) {
            errors[`billingTerms.disposalFees.${index}.unitRate`] = 'Unit rate is required'
        } else if (fee.unitRate < 0) {
            errors[`billingTerms.disposalFees.${index}.unitRate`] = 'Unit rate must not be negative'
        }
        if (fee.estimatedQuantity != null && fee.estimatedQuantity < 0) {
            errors[`billingTerms.disposalFees.${index}.estimatedQuantity`] =
                'Estimated quantity must not be negative'
        }
    })

    values.manualBillableLineItems.forEach((lineItem, index) => {
        if (!lineItem.name.trim()) {
            errors[`manualBillableLineItems.${index}.name`] = 'Name is required'
        }
        if (lineItem.quantity == null || lineItem.quantity <= 0) {
            errors[`manualBillableLineItems.${index}.quantity`] = 'Quantity must be greater than 0'
        }
        if (!lineItem.unit.trim()) {
            errors[`manualBillableLineItems.${index}.unit`] = 'Unit is required'
        }
        if (lineItem.unitPrice == null || lineItem.unitPrice < 0) {
            errors[`manualBillableLineItems.${index}.unitPrice`] = 'Unit price is required'
        }
    })

    return errors
}

export function validateFormValues(
    values: CreateProjectFormValues,
    activeMinimumHourlyRate?: number | null,
) {
    return {
        ...validateCustomer(values),
        ...validateProjectDetails(values),
        ...validateBillingTerms(values, activeMinimumHourlyRate),
    }
}

export {validateBillingTerms}

export function validateStep(
    step: number,
    values: CreateProjectFormValues,
    activeMinimumHourlyRate?: number | null,
) {
    switch (step) {
        case CREATE_PROJECT_STEP.customer:
            return validateCustomer(values)
        case CREATE_PROJECT_STEP.projectDetails:
            return validateProjectDetails(values)
        case CREATE_PROJECT_STEP.billingTerms:
            return validateBillingTerms(values, activeMinimumHourlyRate)
        case CREATE_PROJECT_STEP.review:
            return validateFormValues(values, activeMinimumHourlyRate)
        default:
            return {}
    }
}
